import React from "react";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import {
  generateTimeOptionsMinutePrecision,
  get_t_str_minute_precision,
  filterOptions,
} from "../../../../helpers/helpers";

const OPTION_SWITCH_SX = {
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#6d28d9",
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#6d28d9",
  },
};
const OPTION_SWITCH_LABEL_SX = { marginLeft: "-6px", marginRight: 0 };

// Shared paid-break fields for assigned and open shifts.
export default function PaidBreakInputs({
  paidBreakDurationHours,
  paidBreaksChecked,
  paidBreaks,
  canAddPaidBreak,
  handlePaidBreaksCheckChange,
  handleAddPaidBreak,
  handlePaidBreakChange,
  handleRemovePaidBreak,
  paidBreakValidation,
  startQhnum,
  scheduleDayEndQhnum,
  hasFollowingOption = true,
}) {
  return (
    <div
      className={`pt-2 pb-3 ${
        hasFollowingOption ? "border-b border-slate-200" : ""
      }`}
    >
      <div className="flex h-8 items-center justify-between">
        <div className="flex h-full items-center">
          <FormControlLabel
            sx={OPTION_SWITCH_LABEL_SX}
            control={
              <Switch
                id="paid-breaks-checked"
                name="paidBreaksChecked"
                inputProps={{ "aria-label": "Add paid break" }}
                checked={paidBreaksChecked}
                onChange={handlePaidBreaksCheckChange}
                size="small"
                sx={OPTION_SWITCH_SX}
              />
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handlePaidBreaksCheckChange({
                  target: { name: "paidBreaksChecked", checked: !paidBreaksChecked },
                });
              }
            }}
          />
          <p className="text-slate-900 text-sm ml-1">Add paid break ({paidBreakDurationHours * 60} min)</p>
        </div>
        <div className="h-8 w-8 flex items-center justify-center">
          {paidBreaksChecked && canAddPaidBreak && (
            <Tooltip title="Add another paid break" placement="top">
              <IconButton
                size="small"
                aria-label="Add another paid break"
                onClick={() => handleAddPaidBreak()}
                sx={{
                  color: "#6d28d9",
                  backgroundColor: "#ede9fe",
                  "&:hover": {
                    backgroundColor: "#ddd6fe",
                  },
                  borderRadius: "8px",
                  height: "28px",
                  padding: 0,
                  width: "28px",
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>
      {paidBreaksChecked && (
        <div className="w-full pb-3">
          {paidBreaks.length === 0 ? (
            <p className="text-xs text-slate-500 mt-2">No paid breaks added.</p>
          ) : (
            paidBreaks.map((paidBreak, index) => {
              const breakEnd =
                paidBreak.break_start +
                (paidBreak.break_duration || paidBreakDurationHours);
              return (
                <div
                  key={`${paidBreak.break_start}-${index}`}
                  className="flex items-center gap-2 mt-2"
                >
                  <div className="w-[132px]">
                    <p className="text-sm text-slate-500">Start time</p>
                    <div className="mt-1">
                      <FormControl fullWidth size="small">
                        <Autocomplete
                          disableClearable
                          options={generateTimeOptionsMinutePrecision(
                            startQhnum,
                            1,
                            scheduleDayEndQhnum
                          )}
                          value={get_t_str_minute_precision(
                            paidBreak.break_start
                          )}
                          onChange={(event, newValue) =>
                            handlePaidBreakChange(index, newValue)
                          }
                          filterOptions={filterOptions}
                          size="small"
                          sx={{
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6d28d9",
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              inputProps={{ ...params.inputProps, "aria-label": `Paid break ${index + 1} start time` }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px !important",
                                  height: "45px",
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                      borderColor: "#6d28d9",
                                    },
                                  backgroundColor: "white",
                                },
                              }}
                            />
                          )}
                        />
                      </FormControl>
                    </div>
                  </div>
                  <div className="flex justify-center items-center text-slate-400 mt-6">
                    <ArrowForwardIcon />
                  </div>
                  <div className="w-[132px]">
                    <p className="text-sm text-slate-500">End time</p>
                    <div className="mt-1">
                      <FormControl fullWidth size="small">
                        <TextField
                          value={get_t_str_minute_precision(breakEnd)}
                          disabled
                          size="small"
                          InputProps={{
                            sx: {
                              borderRadius: "8px !important",
                              height: "45px",
                              backgroundColor: "#e2e8f0",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#cbd5e1",
                              },
                            },
                          }}
                        />
                      </FormControl>
                    </div>
                  </div>
                  <IconButton
                    aria-label="Remove paid break"
                    size="small"
                    onClick={() => handleRemovePaidBreak(index)}
                    sx={{ color: "#ef4444", mt: 3 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              );
            })
          )}
          {!paidBreakValidation.valid && (
            <p className="text-xs text-rose-600 mt-2">
              {paidBreakValidation.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
